// Commissions — W5 Phase 3. Surfaces commission runs + the rule-
// snapshot upload flow. Two-column layout mirrors Inbox.tsx (PR #24)
// / Meetings.tsx / Focus.tsx polish standard.
//
// Read shape:
//   - Left column: commissions (filtered by status — pending →
//     approved → failed). Joined to commission_periods for label +
//     date range and to commission_rules for version.
//   - Right column: per-commission detail. For status='validated' the
//     row's approval (`commissions.approval_id`) gets approve/reject
//     buttons; for 'failed' the errors_json is rendered verbatim;
//     for 'approved'/'exported' the CSV download (Phase 4) lands.
//
// Mutation shape:
//   - Approve: UPDATE approvals SET status='approved', decided_by=
//     user.id, decided_at=NOW(). The Commission Agent worker (Phase
//     2) does NOT auto-trigger Gusto CSV on approval — Phase 4
//     wires the exporter into the approval→exported transition.
//   - Reject: UPDATE approvals SET status='rejected'.
//   - Rule snapshot upload (UploadRulesPanel below): client-side
//     xlsx parse via the shared schema → upload .xlsx to the
//     `commission-uploads` Storage bucket → INSERT commission_rules
//     (the IMMUTABLE row carrying rules_json + storage_path). The
//     Sonnet validator picks up the new version on its next tick.
//
// Screen field audit (per CLAUDE.md §19 standard review):
//   - status pill            ← commissions.processing_status
//   - period label           ← commission_periods.period_label
//   - period dates           ← commission_periods.period_start / end
//   - total_amount + currency← commissions.total_amount + currency
//   - line_item_count        ← commissions.line_item_count
//   - rule version           ← commission_rules.version (via FK)
//   - validated timestamp    ← commissions.validated_at
//   - line items table       ← commission_line_items rows for commission_id
//   - errors_json (failed)   ← commissions.errors_json
//   - csv_preview            ← approvals.draft_payload.csv_preview
//   - approve/reject buttons → UPDATE approvals via useMutation
//   - upload errors          ← client-side xlsx parse output (mirrors
//                              workers/agents/commission/excel-intake.ts)

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  CalendarPlus,
  Check,
  ChevronRight,
  DollarSign,
  Download,
  FileSpreadsheet,
  Loader2,
  Upload,
  X,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import GlassCard from '@/components/shared/GlassCard';
import PageStatsRow from '@/components/shared/PageStatsRow';
import PulseDot from '@/components/shared/PulseDot';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';

// =============================================================================
// Types
// =============================================================================

type ProcessingStatus =
  | 'computing'
  | 'validated'
  | 'awaiting_approval'
  | 'approved'
  | 'exported'
  | 'failed';

type CommissionRow = {
  id: string;
  processing_status: ProcessingStatus;
  total_amount: number | null;
  currency: 'USD' | 'CAD' | 'EUR' | 'GBP' | null;
  line_item_count: number | null;
  validated_at: string | null;
  errors_json: Array<{ path: string; message: string; value?: unknown }> | null;
  approval_id: string | null;
  gusto_export_path: string | null;
  gusto_exported_at: string | null;
  trace_id: string;
  created_at: string;
  commission_periods: {
    id: string;
    period_label: string;
    period_start: string;
    period_end: string;
    status: 'open' | 'closed';
  } | null;
  commission_rules: { id: string; version: number; snapshot_filename: string } | null;
};

type LineItemRow = {
  id: string;
  rule_path: string;
  computed_amount: number;
  currency: string;
  line_payload: {
    sku: string | null;
    name: string | null;
    quantity: number;
    employee_email: string;
    tier_name: string;
    rate_pct: number;
  };
};

type ApprovalRow = {
  id: string;
  status: 'pending' | 'approved' | 'edited' | 'rejected' | 'sent' | 'failed';
  decided_at: string | null;
  draft_payload: unknown;
};

const COMMISSION_SELECT = [
  'id, processing_status, total_amount, currency, line_item_count',
  'validated_at, errors_json, approval_id, gusto_export_path, gusto_exported_at',
  'trace_id, created_at',
  'commission_periods:period_id (id, period_label, period_start, period_end, status)',
  'commission_rules:rules_version_id (id, version, snapshot_filename)',
].join(', ');

// =============================================================================
// Fetchers
// =============================================================================

async function fetchCommissions(): Promise<CommissionRow[]> {
  const { data, error } = await supabase
    .from('commissions')
    .select(COMMISSION_SELECT)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .limit(100);
  if (error) throw error;
  return (data as unknown as CommissionRow[]) ?? [];
}

async function fetchLineItems(commissionId: string): Promise<LineItemRow[]> {
  const { data, error } = await supabase
    .from('commission_line_items')
    .select('id, rule_path, computed_amount, currency, line_payload')
    .eq('commission_id', commissionId)
    .order('created_at', { ascending: true })
    .limit(500);
  if (error) throw error;
  return (data as unknown as LineItemRow[]) ?? [];
}

async function fetchApproval(approvalId: string): Promise<ApprovalRow | null> {
  const { data, error } = await supabase
    .from('approvals')
    .select('id, status, decided_at, draft_payload')
    .eq('id', approvalId)
    .maybeSingle();
  if (error) throw error;
  return (data as unknown as ApprovalRow) ?? null;
}

// =============================================================================
// Page
// =============================================================================

const Commissions = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [runOpen, setRunOpen] = useState(false);

  const commissionsQ = useQuery({
    queryKey: ['commissions'],
    queryFn: fetchCommissions,
    refetchInterval: 10_000,
  });

  const commissions = commissionsQ.data ?? [];
  const selected = useMemo(
    () => commissions.find((c) => c.id === selectedId) ?? commissions[0],
    [commissions, selectedId],
  );
  // R2 N1: sync selectedId when the selected row drops out (status
  // transitioned, refetch removed it). Without this, selectedId is
  // stale and the next user interaction can flicker between the
  // fallback `commissions[0]` and the now-missing original.
  useEffect(() => {
    if (selectedId && !commissions.some((c) => c.id === selectedId)) {
      setSelectedId(commissions[0]?.id ?? null);
    }
  }, [selectedId, commissions]);

  const stats = useMemo(() => {
    // R2 SN2: "Pending approval" is a processing_status proxy — counts
    // commissions where the agent finished synthesis but Joel hasn't
    // decided yet. The worker (Phase 2) only writes status='pending'
    // approvals via the RPC, so validated|awaiting_approval ⇒ approval
    // pending in practice. If a future phase introduces async status
    // transitions (Phase 4 adds 'exported'), this proxy still holds:
    // exported rows have processing_status='exported', not 'validated'.
    const pending = commissions.filter(
      (c) => c.processing_status === 'validated' || c.processing_status === 'awaiting_approval',
    ).length;
    const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    const approvedThisMonth = commissions.filter(
      (c) =>
        (c.processing_status === 'approved' || c.processing_status === 'exported') &&
        c.validated_at !== null &&
        c.validated_at >= monthAgo,
    ).length;
    const failed = commissions.filter((c) => c.processing_status === 'failed').length;
    return [
      { label: 'Pending approval', value: pending, icon: DollarSign },
      { label: 'Approved (30d)', value: approvedThisMonth, icon: Check },
      { label: 'Failed validation', value: failed, icon: AlertTriangle },
    ];
  }, [commissions]);

  return (
    <div className="space-y-6">
      <motion.header
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="flex items-start justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-serif font-semibold tracking-tight text-foreground flex items-center gap-3">
            <DollarSign className="w-7 h-7 text-forest" aria-hidden />
            Commissions
          </h1>
          <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
            Per-period commission runs from Joel's three businesses. Approve to ship a Gusto-ready
            CSV; reject if a rule snapshot needs revision.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            type="button"
            onClick={() => setRunOpen(true)}
            className="flex items-center gap-2"
            variant="default"
          >
            <CalendarPlus className="w-4 h-4" aria-hidden />
            Start commission run
          </Button>
          <Button
            type="button"
            onClick={() => setUploadOpen(true)}
            className="flex items-center gap-2"
            variant="outline"
          >
            <Upload className="w-4 h-4" aria-hidden />
            Upload rule snapshot
          </Button>
        </div>
      </motion.header>

      <PageStatsRow stats={stats} />

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-5">
        <CommissionList
          rows={commissions}
          selectedId={selected?.id ?? null}
          onSelect={setSelectedId}
          loading={commissionsQ.isLoading}
        />
        <CommissionDetail commission={selected} ownerId={user?.id ?? null} qc={qc} />
      </div>

      <AnimatePresence>
        {uploadOpen && (
          <UploadRulesPanel
            onClose={() => setUploadOpen(false)}
            ownerId={user?.id ?? null}
            onUploaded={() => {
              setUploadOpen(false);
              qc.invalidateQueries({ queryKey: ['commissions'] });
            }}
          />
        )}
        {runOpen && (
          <StartRunPanel
            onClose={() => setRunOpen(false)}
            ownerId={user?.id ?? null}
            onCreated={() => {
              qc.invalidateQueries({ queryKey: ['commission_periods'] });
              qc.invalidateQueries({ queryKey: ['commissions'] });
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Commissions;

// =============================================================================
// List column
// =============================================================================

function CommissionList(props: {
  rows: CommissionRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  loading: boolean;
}): JSX.Element {
  if (props.loading) {
    return (
      <GlassCard hover={false}>
        <p className="text-sm text-muted-foreground">Loading commissions…</p>
      </GlassCard>
    );
  }
  if (props.rows.length === 0) {
    return (
      <GlassCard hover={false} variant="inset" className="text-center">
        <FileSpreadsheet className="w-10 h-10 text-muted-foreground/60 mx-auto mb-3" aria-hidden />
        <p className="text-sm text-foreground font-medium">No commission runs yet.</p>
        <p className="text-xs text-muted-foreground mt-1">
          Upload a rule snapshot to trigger the agent on the next tick.
        </p>
      </GlassCard>
    );
  }
  return (
    <GlassCard hover={false} className="!p-0 overflow-hidden">
      <ul className="divide-y divide-border">
        {props.rows.map((row) => (
          <CommissionListItem
            key={row.id}
            row={row}
            active={row.id === props.selectedId}
            onSelect={() => props.onSelect(row.id)}
          />
        ))}
      </ul>
    </GlassCard>
  );
}

function CommissionListItem(props: {
  row: CommissionRow;
  active: boolean;
  onSelect: () => void;
}): JSX.Element {
  const tone = statusTone(props.row.processing_status);
  return (
    <li>
      <button
        type="button"
        onClick={props.onSelect}
        className={`w-full text-left px-4 py-3 hover:bg-secondary transition-colors flex items-start gap-3 ${
          props.active ? 'bg-forest-soft/40' : ''
        }`}
      >
        <PulseDot color={tone.dot} size="sm" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-foreground truncate">
              {props.row.commission_periods?.period_label ?? '(no period)'}
            </p>
            <Badge className={tone.badge}>{props.row.processing_status}</Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">
            {props.row.commission_periods
              ? `${props.row.commission_periods.period_start} → ${props.row.commission_periods.period_end}`
              : 'unattached period'}
            {props.row.commission_rules ? ` · rule v${props.row.commission_rules.version}` : ''}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {formatAmount(props.row.total_amount, props.row.currency)}
            {props.row.line_item_count !== null && props.row.line_item_count > 0
              ? ` · ${props.row.line_item_count} line items`
              : ''}
            {' · '}
            {formatDistanceToNow(new Date(props.row.created_at), { addSuffix: true })}
          </p>
        </div>
        <ChevronRight
          className={`w-4 h-4 text-muted-foreground/50 mt-1 flex-shrink-0 ${
            props.active ? 'text-forest' : ''
          }`}
          aria-hidden
        />
      </button>
    </li>
  );
}

// =============================================================================
// Detail column
// =============================================================================

function CommissionDetail(props: {
  commission: CommissionRow | undefined;
  ownerId: string | null;
  qc: ReturnType<typeof useQueryClient>;
}): JSX.Element {
  // R1 B1 fix: dispatch on commission presence WITHOUT calling hooks
  // conditionally. The CommissionDetailLoaded subcomponent owns the
  // useQuery / useMutation calls and only mounts when a commission
  // exists — React unmount/remount handles hook lifecycle correctly
  // across the empty→populated transition.
  if (!props.commission) {
    return (
      <GlassCard
        hover={false}
        variant="inset"
        className="text-center min-h-[200px] flex items-center justify-center"
      >
        <p className="text-sm text-muted-foreground">Select a commission run on the left.</p>
      </GlassCard>
    );
  }
  return (
    <CommissionDetailLoaded commission={props.commission} ownerId={props.ownerId} qc={props.qc} />
  );
}

function CommissionDetailLoaded(props: {
  commission: CommissionRow;
  ownerId: string | null;
  qc: ReturnType<typeof useQueryClient>;
}): JSX.Element {
  const { commission, ownerId, qc } = props;

  const lineItemsQ = useQuery({
    queryKey: ['commission_line_items', commission.id],
    queryFn: () => fetchLineItems(commission.id),
    enabled: commission.processing_status !== 'failed',
  });
  const approvalQ = useQuery({
    queryKey: ['approvals', commission.approval_id],
    queryFn: () => (commission.approval_id ? fetchApproval(commission.approval_id) : null),
    enabled: commission.approval_id !== null,
  });

  const decideMutation = useMutation({
    mutationFn: async ({ decision }: { decision: 'approved' | 'rejected' }) => {
      if (!commission.approval_id) throw new Error('no approval_id on this commission');
      if (!ownerId) throw new Error('not signed in');
      const { error } = await supabase
        .from('approvals')
        .update({
          status: decision,
          decided_by: ownerId,
          decided_at: new Date().toISOString(),
        })
        .eq('id', commission.approval_id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commissions'] });
      qc.invalidateQueries({ queryKey: ['approvals', commission.approval_id] });
    },
  });

  const tone = statusTone(commission.processing_status);
  const lineItems = lineItemsQ.data ?? [];
  const approval = approvalQ.data ?? null;

  return (
    <GlassCard hover={false} className="space-y-4">
      <header className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
            {commission.commission_periods?.period_label ?? '(no period)'}
          </p>
          <h2 className="text-2xl font-semibold text-foreground mt-1">
            {formatAmount(commission.total_amount, commission.currency)}
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            {commission.line_item_count ?? 0} line items
            {commission.commission_rules
              ? ` · against rule v${commission.commission_rules.version} (${commission.commission_rules.snapshot_filename})`
              : ''}
            {commission.validated_at
              ? ` · validated ${formatDistanceToNow(new Date(commission.validated_at), {
                  addSuffix: true,
                })}`
              : ''}
          </p>
        </div>
        <Badge className={tone.badge}>{commission.processing_status}</Badge>
      </header>

      {/* Failed branch: render errors_json verbatim */}
      {commission.processing_status === 'failed' && (
        <FailedErrorsCard errors={commission.errors_json ?? []} />
      )}

      {/* Validated branch: line items + actions */}
      {commission.processing_status !== 'failed' && (
        <>
          <LineItemTable
            items={lineItems}
            loading={lineItemsQ.isLoading}
            totalCount={commission.line_item_count}
          />
          <CsvPreviewCard
            approval={approval}
            loading={approvalQ.isLoading}
            totalLineItems={commission.line_item_count}
          />
        </>
      )}

      {/* Action buttons — only visible when an approval is pending */}
      {(commission.processing_status === 'validated' ||
        commission.processing_status === 'awaiting_approval' ||
        commission.processing_status === 'failed') &&
        approval?.status === 'pending' && (
          <div className="flex flex-col sm:flex-row gap-2 pt-3 mt-2 border-t border-border">
            {commission.processing_status !== 'failed' && (
              <Button
                type="button"
                onClick={() => decideMutation.mutate({ decision: 'approved' })}
                disabled={decideMutation.isPending}
                className="flex-1 flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" aria-hidden />
                Approve
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => decideMutation.mutate({ decision: 'rejected' })}
              disabled={decideMutation.isPending}
              className="flex-1 flex items-center justify-center gap-2"
            >
              <X className="w-4 h-4" aria-hidden />
              Reject
            </Button>
          </div>
        )}

      {commission.processing_status === 'exported' && (
        <GustoExportCard
          gustoExportPath={commission.gusto_export_path}
          gustoExportedAt={commission.gusto_exported_at}
        />
      )}
    </GlassCard>
  );
}

// =============================================================================
// GustoExportCard — Download CSV button (W5 Phase 4)
// =============================================================================
//
// Creates a signed URL for the gusto_export_path object in the
// commission-exports bucket and triggers a browser download. The
// signed URL has a 1-hour expiry; the user can re-click to mint a new
// one if the prior expired (cheap operation, no Storage write).
//
// RLS: the bucket policy commission_exports_owner_all (migration
// 0027) matches storage.objects on (storage.foldername(name))[1] =
// auth.uid()::TEXT. The signed-URL endpoint runs as the authenticated
// owner via supabase-js, so the policy gates which paths can sign.
//
// Screen-field audit:
//   - "CSV exported X ago"  ← commissions.gusto_exported_at
//                             (writer: gusto-exporter.ts on UPDATE)
//   - Download button       ← commissions.gusto_export_path
//                             (writer: gusto-exporter.ts on UPDATE)

function GustoExportCard(props: {
  gustoExportPath: string | null;
  gustoExportedAt: string | null;
}): JSX.Element {
  const { gustoExportPath, gustoExportedAt } = props;
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onDownload(): Promise<void> {
    if (!gustoExportPath) return;
    setDownloading(true);
    setError(null);
    try {
      const { data, error: signErr } = await supabase.storage
        .from('commission-exports')
        .createSignedUrl(gustoExportPath, 60 * 60); // 1 hour
      if (signErr) throw signErr;
      if (!data?.signedUrl) throw new Error('No signed URL returned from Storage');
      // Anchor-click download keeps the file in the bucket; opens a
      // standard browser save. Adding `download` triggers the file-
      // download UI instead of an inline render.
      const a = document.createElement('a');
      a.href = data.signedUrl;
      // Hint filename — uses the Storage object's leaf name which is
      // built by gusto-exporter as
      //   `<company-slug>-timesheet-<pay-date>.csv`
      // (verified against Joel's 2026-06-02 medicom-llc sample). The
      // first path segment is the owner_id and stays in the bucket
      // path; only the filename portion is downloaded.
      const filename = gustoExportPath.split('/').pop() ?? 'commission.csv';
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="neo-inset p-3">
      <p className="text-xs text-muted-foreground uppercase tracking-wide">Gusto export</p>
      <p className="text-sm text-foreground mt-1">
        CSV exported{' '}
        {gustoExportedAt ? formatDistanceToNow(new Date(gustoExportedAt), { addSuffix: true }) : ''}
      </p>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={onDownload}
        disabled={downloading || !gustoExportPath}
        className="mt-2"
      >
        {downloading ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden />
        ) : (
          <Download className="w-4 h-4 mr-2" aria-hidden />
        )}
        {downloading ? 'Generating link…' : 'Download CSV'}
      </Button>
      <p className="mt-1 text-[10px] text-muted-foreground/80">
        Signed URL · 1-hour expiry · owner-only via RLS
      </p>
      {error && (
        <p className="mt-2 text-xs text-alert" role="alert">
          Couldn&apos;t generate download link: {error}
        </p>
      )}
    </div>
  );
}

function FailedErrorsCard(props: {
  errors: Array<{ path: string; message: string; value?: unknown }>;
}): JSX.Element {
  return (
    <div className="neo-inset p-3">
      <p className="text-xs uppercase tracking-wide text-alert">
        {props.errors.length} validation error{props.errors.length === 1 ? '' : 's'}
      </p>
      <ul className="mt-2 space-y-2">
        {props.errors.map((err, i) => (
          <li key={i} className="text-sm">
            <p className="font-mono text-xs text-muted-foreground">{err.path}</p>
            <p className="text-foreground">{err.message}</p>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-muted-foreground">
        Upload a corrected rule snapshot — the agent will re-run on the next tick (300s).
      </p>
    </div>
  );
}

function LineItemTable(props: {
  items: LineItemRow[];
  loading: boolean;
  totalCount: number | null;
}): JSX.Element {
  if (props.loading) {
    return <p className="text-sm text-muted-foreground">Loading line items…</p>;
  }
  if (props.items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic">
        No line items recorded for this commission.
      </p>
    );
  }
  // R1 SN2 fix: surface a "more exist — truncated" hint when the
  // .limit(500) fetch hit its cap. Joel still sees the full amount
  // via commission.total_amount + the Gusto CSV (Phase 4) carries
  // every line item; the table is just preview.
  const truncated = isLineItemListTruncated(props.items.length, props.totalCount);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border">
            <th className="py-2 pr-2">Employee</th>
            <th className="py-2 pr-2">Tier</th>
            <th className="py-2 pr-2">Product</th>
            <th className="py-2 pr-2 text-right">Qty</th>
            <th className="py-2 pr-2 text-right">Rate</th>
            <th className="py-2 pr-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {props.items.map((li) => (
            <tr key={li.id} className="hover:bg-secondary/40">
              <td className="py-1.5 pr-2 truncate max-w-[180px]">
                {li.line_payload.employee_email}
              </td>
              <td className="py-1.5 pr-2 text-muted-foreground">{li.line_payload.tier_name}</td>
              <td className="py-1.5 pr-2 truncate max-w-[200px]">
                {li.line_payload.name ?? li.line_payload.sku ?? '(no product)'}
              </td>
              <td className="py-1.5 pr-2 text-right">{li.line_payload.quantity}</td>
              <td className="py-1.5 pr-2 text-right">{li.line_payload.rate_pct}%</td>
              <td className="py-1.5 pr-2 text-right font-mono">
                {formatAmount(
                  li.computed_amount,
                  (li.currency ?? null) as CommissionRow['currency'],
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {truncated && (
        <p className="mt-2 text-xs text-muted-foreground italic">
          Showing first {props.items.length} of {props.totalCount}. The full set lands in the Gusto
          CSV (Phase 4).
        </p>
      )}
    </div>
  );
}

function CsvPreviewCard(props: {
  approval: ApprovalRow | null;
  loading: boolean;
  totalLineItems: number | null;
}): JSX.Element | null {
  if (props.loading) return null;
  if (!props.approval) return null;
  const payload = props.approval.draft_payload as { kind?: string; csv_preview?: unknown } | null;
  if (!payload || payload.kind !== 'ready' || !Array.isArray(payload.csv_preview)) return null;
  const csvPreview = payload.csv_preview as Array<{
    rule_path: string;
    amount: number;
    currency: string;
    employee: string;
  }>;
  if (csvPreview.length === 0) return null;
  // R1 SN1 fix: "first N of TOTAL" using the truth from commissions.line_item_count
  // (worker.ts:483 hard-caps the preview at 10; the table below the
  // preview shows the rest).
  const total = props.totalLineItems ?? csvPreview.length;
  return (
    <div className="neo-inset p-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        Gusto CSV preview (first {csvPreview.length} of {total})
      </p>
      <ul className="mt-2 space-y-1 font-mono text-xs">
        {csvPreview.map((row, i) => (
          <li key={i} className="text-foreground">
            {/* R1 N3 fix: split(' for ')[0] always returns at least one element;
                the ?? fallback is dead. Use [0] directly. */}
            {row.employee} · {row.rule_path.split(' for ')[0]} ·{' '}
            {formatAmount(row.amount, row.currency as CommissionRow['currency'])}
          </li>
        ))}
      </ul>
    </div>
  );
}

// =============================================================================
// Upload Rules modal
// =============================================================================

function UploadRulesPanel(props: {
  onClose: () => void;
  onUploaded: () => void;
  ownerId: string | null;
}): JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [parseResult, setParseResult] = useState<
    | { status: 'idle' }
    | { status: 'parsing' }
    | { status: 'invalid'; errors: Array<{ path: string; message: string }> }
    | { status: 'valid'; rules: { tiers: unknown[]; product_map: unknown[] } }
    | { status: 'uploading' }
    | { status: 'error'; message: string }
  >({ status: 'idle' });

  // R1 N1 + R2 SN3 fix: Escape-to-close modal. deps=[props.onClose]
  // so the listener is registered once per onClose identity (the
  // parent provides a stable reference via setUploadOpen(false)),
  // not on every parent re-render.
  const onClose = props.onClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onFileSelect = async (f: File | null) => {
    setFile(f);
    if (!f) {
      setParseResult({ status: 'idle' });
      return;
    }
    setParseResult({ status: 'parsing' });
    try {
      const buffer = await f.arrayBuffer();
      const result = parseWorkbookClient(buffer);
      if (result.status === 'invalid') {
        setParseResult({ status: 'invalid', errors: result.errors });
      } else {
        setParseResult({ status: 'valid', rules: result.rules });
      }
    } catch (err) {
      setParseResult({
        status: 'error',
        message: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const upload = async () => {
    if (parseResult.status !== 'valid' || !file || !props.ownerId) return;
    setParseResult({ status: 'uploading' });
    try {
      // Next version = max(existing) + 1.
      const { data: existing, error: vErr } = await supabase
        .from('commission_rules')
        .select('version')
        .eq('owner_id', props.ownerId)
        .order('version', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (vErr) throw vErr;
      const nextVersion = ((existing as { version?: number } | null)?.version ?? 0) + 1;

      // R1 N5 fix: sanitize the filename for Storage path safety —
      // spaces / non-ASCII / quotes break signed-URL referencing
      // downstream. Whitelist letters / digits / .-_ ; collapse the
      // rest to underscore. Preserves the extension.
      const safeName = file.name.replace(/[^A-Za-z0-9._-]/g, '_');
      const storagePath = `${props.ownerId}/${nextVersion}-${safeName}`;
      const { error: upErr } = await supabase.storage
        .from('commission-uploads')
        .upload(storagePath, file, { upsert: false });
      if (upErr) throw upErr;

      const { error: insErr } = await supabase.from('commission_rules').insert({
        owner_id: props.ownerId,
        version: nextVersion,
        rules_json: parseResult.rules,
        snapshot_filename: file.name,
        snapshot_storage_path: storagePath,
        trace_id: crypto.randomUUID(),
      });
      if (insErr) {
        // R2 N2: clean up the orphaned storage object so the bucket
        // doesn't fill with garbage on a UNIQUE(owner_id, version)
        // collision (two tabs racing the same nextVersion). Best-
        // effort — if remove fails, the file lingers but RLS keeps
        // it private to Joel.
        await supabase.storage
          .from('commission-uploads')
          .remove([storagePath])
          .catch(() => {});
        throw insErr;
      }
      props.onUploaded();
    } catch (err) {
      setParseResult({
        status: 'error',
        message: err instanceof Error ? err.message : String(err),
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={props.onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-card border border-border rounded-xl shadow-xl max-w-2xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Upload rule snapshot</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Excel (.xlsx) with two sheets: <strong>Tiers</strong> and <strong>Products</strong>.
              See the agent's prompt for the column shape.
            </p>
          </div>
          <button
            type="button"
            onClick={props.onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            <X className="w-5 h-5" aria-hidden />
          </button>
        </header>

        <input
          type="file"
          accept=".xlsx"
          onChange={(e) => onFileSelect(e.target.files?.[0] ?? null)}
          className="block w-full text-sm text-foreground file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-forest file:text-primary-foreground file:cursor-pointer file:hover:bg-forest/90"
        />

        {parseResult.status === 'parsing' && (
          <p className="text-sm text-muted-foreground">Parsing…</p>
        )}
        {parseResult.status === 'invalid' && (
          <div className="neo-inset p-3">
            <p className="text-xs uppercase tracking-wide text-alert">
              {parseResult.errors.length} parse error{parseResult.errors.length === 1 ? '' : 's'}
            </p>
            <ul className="mt-2 space-y-2">
              {parseResult.errors.map((err, i) => (
                <li key={i} className="text-sm">
                  <p className="font-mono text-xs text-muted-foreground">{err.path}</p>
                  <p className="text-foreground">{err.message}</p>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-muted-foreground">
              Fix the file and try again. The agent never sees a partial rule set (N7).
            </p>
          </div>
        )}
        {parseResult.status === 'valid' && (
          <div className="neo-inset p-3">
            <p className="text-xs uppercase tracking-wide text-forest">Parse OK</p>
            <p className="text-sm text-foreground mt-1">
              {file?.name} parsed cleanly —{' '}
              {/* R1 N4 fix: surface counts so Joel catches "uploaded the
                  wrong file" before triggering the agent. */}
              <strong>{parseResult.rules.tiers.length}</strong> tier
              {parseResult.rules.tiers.length === 1 ? '' : 's'},{' '}
              <strong>{parseResult.rules.product_map.length}</strong> product mapping
              {parseResult.rules.product_map.length === 1 ? '' : 's'}.
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Sonnet will re-validate semantically on the next tick.
            </p>
          </div>
        )}
        {parseResult.status === 'uploading' && (
          <p className="text-sm text-muted-foreground">Uploading + saving…</p>
        )}
        {parseResult.status === 'error' && (
          <div className="neo-inset p-3">
            <p className="text-xs uppercase tracking-wide text-alert">Upload failed</p>
            <p className="text-sm text-foreground mt-1 font-mono">{parseResult.message}</p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <Button type="button" variant="outline" onClick={props.onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={upload}
            disabled={parseResult.status !== 'valid' || !props.ownerId}
          >
            Upload as next version
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// =============================================================================
// Start commission run — creates a commission_periods row (R-COMM-PERIOD).
// Owner-INSERT is already permitted by commission_periods_owner_insert
// (migration 0027); no service-role write, no money moves here — a period is
// just the window the agent later computes a commission over.
// =============================================================================

// All non-deleted periods, open AND closed. We need closed ones too: the
// overlap guard must catch a new window that re-pulls orders already paid in a
// closed period (worker attributes orders by date, with no cross-period dedup).
async function fetchExistingPeriods(): Promise<ExistingPeriod[]> {
  const { data, error } = await supabase
    .from('commission_periods')
    .select('id, period_label, period_start, period_end, status')
    .is('deleted_at', null)
    .order('period_end', { ascending: false })
    .limit(100);
  if (error) throw error;
  return (data as unknown as ExistingPeriod[]) ?? [];
}

function StartRunPanel(props: {
  onClose: () => void;
  onCreated: () => void;
  ownerId: string | null;
}): JSX.Element {
  const [label, setLabel] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [acknowledgeOverlap, setAcknowledgeOverlap] = useState(false);
  const [submitState, setSubmitState] = useState<
    | { status: 'idle' }
    | { status: 'submitting' }
    | { status: 'success' }
    | { status: 'error'; message: string }
  >({ status: 'idle' });

  const onClose = props.onClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Existing periods (open + closed) — needed to flag a duplicate/overlapping
  // window before INSERT. We must NOT submit until this resolves: an empty set
  // while loading/errored would wave an overlapping (double-pay) window through.
  const existingPeriodsQ = useQuery({
    queryKey: ['commission_periods', 'existing'],
    queryFn: fetchExistingPeriods,
  });
  const existingPeriods = existingPeriodsQ.data ?? [];

  const errors = validatePeriodForm({ label, start, end });
  const formValid = periodFormIsValid(errors);
  const conflicts = formValid
    ? findWindowConflicts(start, end, existingPeriods)
    : { exactDuplicate: false, overlaps: [] };
  const needsOverlapAck = conflicts.overlaps.length > 0;
  const closedOverlaps = conflicts.overlaps.filter((p) => p.status === 'closed');

  // Reset the overlap acknowledgement whenever the window changes so consent
  // from a previous (different) overlapping window can't silently carry over.
  useEffect(() => {
    setAcknowledgeOverlap(false);
  }, [start, end]);

  const canSubmit = canCreatePeriod({
    formValid,
    periodsLoaded: existingPeriodsQ.isSuccess,
    exactDuplicate: conflicts.exactDuplicate,
    needsOverlapAck,
    acknowledgeOverlap,
    hasOwner: !!props.ownerId,
    submitting: submitState.status === 'submitting',
  });

  const submit = async () => {
    if (!canSubmit || !props.ownerId) return;
    setSubmitState({ status: 'submitting' });
    try {
      const { error } = await supabase.from('commission_periods').insert({
        owner_id: props.ownerId,
        period_label: label.trim(),
        period_start: start,
        period_end: end,
        status: 'open',
        source_channel: 'manual',
        trace_id: crypto.randomUUID(),
      });
      if (error) {
        // 23505 = unique_violation on (owner_id, period_start, period_end) —
        // a race or a window created in another tab. Surface it cleanly
        // instead of a raw Postgres error.
        const friendly =
          (error as { code?: string }).code === '23505'
            ? 'A period with these exact start and end dates already exists.'
            : error.message;
        setSubmitState({ status: 'error', message: friendly });
        return;
      }
      props.onCreated();
      setSubmitState({ status: 'success' });
    } catch (err) {
      setSubmitState({
        status: 'error',
        message: err instanceof Error ? err.message : String(err),
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={props.onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-card border border-border rounded-xl shadow-xl max-w-2xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Start a commission run</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Opens a commission period. The Commission Agent picks up open periods on its next tick
              and drafts a Gusto-ready CSV — provided a rule snapshot is uploaded.
            </p>
          </div>
          <button
            type="button"
            onClick={props.onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            <X className="w-5 h-5" aria-hidden />
          </button>
        </header>

        {submitState.status === 'success' ? (
          <div className="neo-inset p-3 space-y-2">
            <p className="text-xs uppercase tracking-wide text-forest">Period created</p>
            <p className="text-sm text-foreground">
              <strong>{label.trim()}</strong> ({start} → {end}) is now open.
            </p>
            <p className="text-xs text-muted-foreground">
              The Commission Agent will draft a commission for this window on its next tick if a
              rule snapshot exists. It won't appear in the list until then.
            </p>
            <div className="flex justify-end pt-2">
              <Button type="button" onClick={props.onClose}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              <div className="space-y-1">
                <label htmlFor="period-label" className="text-sm font-medium text-foreground">
                  Period label
                </label>
                <Input
                  id="period-label"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. Q2 2026 Reverra Health"
                  maxLength={MAX_PERIOD_LABEL_LENGTH}
                  aria-invalid={!!errors.label}
                />
                {errors.label && <p className="text-xs text-alert">{errors.label}</p>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label htmlFor="period-start" className="text-sm font-medium text-foreground">
                    Start date
                  </label>
                  <Input
                    id="period-start"
                    type="date"
                    value={start}
                    onChange={(e) => setStart(e.target.value)}
                    aria-invalid={!!errors.start}
                  />
                  {errors.start && <p className="text-xs text-alert">{errors.start}</p>}
                </div>
                <div className="space-y-1">
                  <label htmlFor="period-end" className="text-sm font-medium text-foreground">
                    End date
                  </label>
                  <Input
                    id="period-end"
                    type="date"
                    value={end}
                    onChange={(e) => setEnd(e.target.value)}
                    aria-invalid={!!errors.end}
                  />
                  {errors.end && <p className="text-xs text-alert">{errors.end}</p>}
                </div>
              </div>
            </div>

            {/* We can't safely check overlaps until the existing-periods list
                loads — block + say so rather than silently waving a window
                through. canCreatePeriod also gates submit on isSuccess. */}
            {existingPeriodsQ.isError && (
              <div className="neo-inset p-3">
                <p className="text-xs uppercase tracking-wide text-alert">
                  Couldn't load existing periods
                </p>
                <p className="text-sm text-foreground mt-1">
                  We can't check for overlapping windows right now, so creating a run is disabled.
                  Close and reopen this panel to retry.
                </p>
              </div>
            )}
            {existingPeriodsQ.isLoading && (
              <p className="text-xs text-muted-foreground">Loading existing periods…</p>
            )}

            {/* Existing periods — context so Joel can see what's already queued
                (and what's already been paid) and avoid an accidental dup/overlap. */}
            {existingPeriods.length > 0 && (
              <div className="neo-inset px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
                  {existingPeriods.length} existing period{existingPeriods.length === 1 ? '' : 's'}
                </p>
                <ul className="space-y-1">
                  {existingPeriods.map((p) => (
                    <li key={p.id} className="text-xs text-foreground flex items-center gap-2">
                      <span className="font-medium truncate">{p.period_label}</span>
                      <span className="font-mono text-muted-foreground shrink-0">
                        {p.period_start} → {p.period_end}
                      </span>
                      <span
                        className={`neo-chip ml-auto shrink-0 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
                          p.status === 'closed'
                            ? 'bg-forest-soft text-forest'
                            : 'bg-attention/15 text-attention'
                        }`}
                      >
                        {p.status === 'closed' ? 'closed · paid' : 'open'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {conflicts.exactDuplicate && (
              <div className="neo-inset p-3">
                <p className="text-xs uppercase tracking-wide text-alert">Duplicate window</p>
                <p className="text-sm text-foreground mt-1">
                  A period already covers exactly {start} → {end}. Adjust the dates or use the
                  existing period.
                </p>
              </div>
            )}

            {needsOverlapAck && !conflicts.exactDuplicate && (
              <div className="neo-inset p-3">
                <p className="text-xs uppercase tracking-wide text-attention">
                  Overlaps {conflicts.overlaps.length} period
                  {conflicts.overlaps.length === 1 ? '' : 's'}
                  {closedOverlaps.length > 0 ? ` (${closedOverlaps.length} already paid)` : ''}
                </p>
                <p className="text-sm text-foreground mt-1">
                  This window overlaps {conflicts.overlaps.map((p) => p.period_label).join(', ')}.
                  Overlapping periods can count the same Shopify orders into two payouts
                  {closedOverlaps.length > 0
                    ? ' — and the overlapping closed period(s) were already paid out, so those orders may be paid twice'
                    : ''}
                  . Only proceed if that's intended.
                </p>
                <label className="mt-2 flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={acknowledgeOverlap}
                    onChange={(e) => setAcknowledgeOverlap(e.target.checked)}
                    className="h-4 w-4 rounded border-border"
                  />
                  I understand these windows overlap — create anyway.
                </label>
              </div>
            )}

            {submitState.status === 'error' && (
              <div className="neo-inset p-3">
                <p className="text-xs uppercase tracking-wide text-alert">
                  Could not create period
                </p>
                <p className="text-sm text-foreground mt-1 font-mono">{submitState.message}</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <Button type="button" variant="outline" onClick={props.onClose}>
                Cancel
              </Button>
              <Button type="button" onClick={submit} disabled={!canSubmit}>
                {submitState.status === 'submitting' ? 'Creating…' : 'Start run'}
              </Button>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

// =============================================================================
// Client-side workbook parsing — MIRRORS apps/workers/src/agents/commission/
// excel-intake.ts. Kept in sync manually; reviewer's screen-field-audit
// catches drift. See R2 R1b SN2 — the LLM validator schema also enforces
// minItems on tiers + product_map; this client-side check fails fast.
// =============================================================================

type ClientParseResult =
  | { status: 'valid'; rules: { tiers: unknown[]; product_map: unknown[] } }
  | { status: 'invalid'; errors: Array<{ path: string; message: string }> };

function parseWorkbookClient(buffer: ArrayBuffer): ClientParseResult {
  let wb: XLSX.WorkBook;
  try {
    wb = XLSX.read(buffer, { type: 'array' });
  } catch (err) {
    return {
      status: 'invalid',
      errors: [
        {
          path: 'workbook',
          message: `Could not read workbook: ${err instanceof Error ? err.message : String(err)}`,
        },
      ],
    };
  }
  const errors: Array<{ path: string; message: string }> = [];
  const tiersSheet = wb.Sheets['Tiers'];
  const productsSheet = wb.Sheets['Products'];
  if (!tiersSheet) {
    errors.push({ path: 'Tiers!sheet', message: 'Required sheet "Tiers" not found' });
  }
  if (!productsSheet) {
    errors.push({ path: 'Products!sheet', message: 'Required sheet "Products" not found' });
  }
  if (errors.length) return { status: 'invalid', errors };

  const tiers = XLSX.utils.sheet_to_json(tiersSheet!, { defval: null, raw: true });
  const products = XLSX.utils.sheet_to_json(productsSheet!, { defval: null, raw: true });

  if (!tiers.length)
    errors.push({ path: 'Tiers!header', message: 'Sheet "Tiers" has no data rows' });
  if (!products.length)
    errors.push({ path: 'Products!header', message: 'Sheet "Products" has no data rows' });

  // Light structural pass — the workers-side `excel-intake.ts` does
  // the heavy validation per N7. Client-side surfaces the obvious
  // errors immediately so Joel doesn't wait for the next tick to
  // discover them.
  if (errors.length) return { status: 'invalid', errors };

  return {
    status: 'valid',
    rules: { tiers: tiers as unknown[], product_map: products as unknown[] },
  };
}

// =============================================================================
// Helpers
// =============================================================================

function formatAmount(
  amount: number | null,
  currency: CommissionRow['currency'] | string | null,
): string {
  if (amount === null || amount === undefined) return '—';
  if (!currency) return amount.toFixed(2);
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

// Pure helper exported for tests. Returns true when the fetched
// list hit its .limit() cap below the DB-reported total (R2 SN1
// scaling-fixture regression test exercises both branches).
export function isLineItemListTruncated(
  fetchedCount: number,
  totalCount: number | null | undefined,
): boolean {
  if (totalCount === null || totalCount === undefined) return false;
  return fetchedCount < totalCount;
}

// =============================================================================
// Commission-period creation helpers (R-COMM-PERIOD, 2026-06-18).
//
// The Commission Agent's loadEligiblePeriod (apps/workers/src/agents/commission/
// worker.ts) only ever READS commission_periods — until this surface, the only
// writers were W5 smoke scripts, so on a fresh deployment the agent was inert
// (no open period → null forever). These pure helpers back the "Start
// commission run" panel. Money-path: a period defines the order window a
// commission is computed over, so overlapping windows risk double-counting the
// same Shopify orders into two payouts — hence the explicit overlap guard.
// All dates are ISO 'YYYY-MM-DD' (HTML date inputs + the DATE columns), where
// lexicographic and chronological order coincide.
// =============================================================================

export type PeriodFormInput = { label: string; start: string; end: string };
export type PeriodFormErrors = { label?: string; start?: string; end?: string };

// A period label is owner-facing metadata that also rides into the Gusto CSV
// personal-note column (csvEscape handles delimiters, so this bound is hygiene,
// not injection defense). A commission period spanning > ~1 year is almost
// certainly a fat-fingered date that would sweep far more orders than intended,
// so we cap the window — split long ranges into multiple runs instead.
export const MAX_PERIOD_LABEL_LENGTH = 120;
export const MAX_PERIOD_DAYS = 366;

export function validatePeriodForm(input: PeriodFormInput): PeriodFormErrors {
  const errors: PeriodFormErrors = {};
  const label = input.label.trim();
  if (!label) errors.label = 'A period label is required.';
  else if (label.length > MAX_PERIOD_LABEL_LENGTH)
    errors.label = `Keep the label under ${MAX_PERIOD_LABEL_LENGTH} characters.`;
  if (!input.start) errors.start = 'Start date is required.';
  if (!input.end) errors.end = 'End date is required.';
  // Only assert ordering / span once both dates are present so we don't show a
  // confusing "end before start" while the user is still filling the form.
  if (input.start && input.end) {
    if (input.end < input.start) {
      errors.end = 'End date must be on or after the start date.';
    } else {
      const spanDays = (Date.parse(input.end) - Date.parse(input.start)) / 86_400_000;
      if (spanDays > MAX_PERIOD_DAYS) {
        errors.end = `A commission period can't span more than ${MAX_PERIOD_DAYS} days. Split it into multiple runs.`;
      }
    }
  }
  return errors;
}

export function periodFormIsValid(errors: PeriodFormErrors): boolean {
  return !errors.label && !errors.start && !errors.end;
}

export type ExistingPeriod = {
  id: string;
  period_label: string;
  period_start: string;
  period_end: string;
  status: 'open' | 'closed';
};

// Inclusive date windows overlap iff aStart <= bEnd AND bStart <= aEnd.
export function periodsOverlap(
  aStart: string,
  aEnd: string,
  bStart: string,
  bEnd: string,
): boolean {
  return aStart <= bEnd && bStart <= aEnd;
}

// Classifies a candidate window against ALL existing (non-deleted) periods —
// open AND closed. Closed periods matter for the overlap check because the
// worker attributes orders to a window purely by date (worker.ts
// loadOrdersForPeriod), so a new OPEN window overlapping an already-CLOSED
// (paid + exported) one would re-pull the same Shopify orders into a second
// payout. The guard must therefore see closed periods too, even though the
// agent itself only generates commissions for open ones.
//   - exactDuplicate: same start AND end (the DB UNIQUE(owner,start,end) would
//     reject this anyway — we surface it cleanly instead of a raw 23505).
//   - overlaps: windows that intersect but aren't identical — a soft warning
//     the owner must explicitly acknowledge before we INSERT (double-pay risk).
export function findWindowConflicts(
  start: string,
  end: string,
  existing: ExistingPeriod[],
): { exactDuplicate: boolean; overlaps: ExistingPeriod[] } {
  let exactDuplicate = false;
  const overlaps: ExistingPeriod[] = [];
  for (const p of existing) {
    if (p.period_start === start && p.period_end === end) {
      exactDuplicate = true;
    } else if (periodsOverlap(start, end, p.period_start, p.period_end)) {
      overlaps.push(p);
    }
  }
  return { exactDuplicate, overlaps };
}

// Submit-gate for the Start-run panel, extracted as a pure fn so the
// money-path "never INSERT before the overlap set is known" rule is unit-
// tested (the loading/error race a render test would otherwise leave
// unexercised). periodsLoaded MUST be true — if the existing-periods query is
// still loading or errored, the overlap guard is computed against an empty set
// and would wave through an overlapping (double-pay) window.
export type PeriodSubmitGate = {
  formValid: boolean;
  periodsLoaded: boolean;
  exactDuplicate: boolean;
  needsOverlapAck: boolean;
  acknowledgeOverlap: boolean;
  hasOwner: boolean;
  submitting: boolean;
};

export function canCreatePeriod(g: PeriodSubmitGate): boolean {
  return (
    g.formValid &&
    g.periodsLoaded &&
    !g.exactDuplicate &&
    (!g.needsOverlapAck || g.acknowledgeOverlap) &&
    g.hasOwner &&
    !g.submitting
  );
}

function statusTone(status: ProcessingStatus): {
  badge: string;
  dot: 'green' | 'amber' | 'red';
} {
  switch (status) {
    case 'approved':
    case 'exported':
      return { badge: 'neo-chip bg-forest-soft text-forest', dot: 'green' };
    case 'validated':
    case 'awaiting_approval':
      return { badge: 'neo-chip bg-attention/15 text-attention', dot: 'amber' };
    case 'failed':
      return { badge: 'neo-chip bg-alert/15 text-alert', dot: 'red' };
    default:
      return { badge: 'neo-chip bg-muted text-muted-foreground', dot: 'amber' };
  }
}
